// Package imports:
import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faCopy, faCheck } from "@fortawesome/pro-solid-svg-icons";
// Component imports:
import HeadSection from "../../ui-elements/HeadSection/HeadSection";
import Instructions from "./Instructions";
import DisplayBox from "../../ui-elements/DisplayBox/DisplayBox";
import CompaniesTable from "./CompaniesTable";
import Tabs from "../../ui-elements/Tabs/Tabs";
import IndividualsTable from "./IndividualsTable";
import KYCNavBar from "../Company/KYCNavBar/KYCNavBar";
import KYCSettings from "./KYCSettings/KYCSettings";
import Alert from "../../ui-elements/Alert/Alert";
import Tooltip from "../../ui-elements/Tooltip/Tooltip";
// Type imports:
import { DueDiligenceViewModel, KnowYourCustomer, KnowYourIndividual, ServiceCompanyEmployee } from "../../types/DueDiligenceTypes";
import { Fetched } from "../../types/Types";
import Link from "../../ui-elements/Link/Link";

const KnowYourCustomerPage: React.FC<DueDiligenceViewModel> = ({
    employees,
    companyKYCPrice,
    individualKYCPrice,
    hasCustomWebPage,
    isKycAdmin,
    customPageSlug
}) => {
    return (
        <div className="KCL_due_diligence_page">
            <KYCNavBar />
            <div className="section--white page_body">
                <AllSurveyComp
                    employees={employees}
                    companyKYCPrice={companyKYCPrice}
                    individualKYCPrice={individualKYCPrice}
                    hasCustomWebPage={hasCustomWebPage}
                    isKycAdmin={isKycAdmin}
                    customPageSlug={customPageSlug}
                />
            </div>
        </div>
    )
};

export default KnowYourCustomerPage;

interface IAllSurveyCompProps {
    employees: ServiceCompanyEmployee[];
    companyKYCPrice: number;
    individualKYCPrice: number;
    hasCustomWebPage: boolean;
    isKycAdmin: boolean;
    customPageSlug: string | null;
}
const AllSurveyComp: React.FC<IAllSurveyCompProps> = ({ employees, companyKYCPrice, individualKYCPrice, hasCustomWebPage, isKycAdmin, customPageSlug }) => {
    // Initialize view from URL hash
    const getInitialView = (): 'companies' | 'individuals' | 'settings' => {
        const hash = window.location.hash.replace('#', '');
        if (hash === 'settings') return 'settings';
        if (hash === 'individuals') return 'individuals';
        return 'companies';
    };
    const [activeTab, setActiveTab] = useState<'companies' | 'individuals'>(
        () => {
            const hash = window.location.hash.replace('#', '');
            return hash === 'individuals' ? 'individuals' : 'companies';
        }
    );
    const [showSettings, setShowSettings] = useState<boolean>(getInitialView() === 'settings');
    const [customers, setCustomers] = useState<Fetched<KnowYourCustomer[]>>([]);
    const [individuals, setIndividuals] = useState<Fetched<KnowYourIndividual[]>>([]);
    const [showKYCOverview, setShowKYCOverview] = useState(false);

    // Listen for browser back/forward and update view accordingly
    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash.replace('#', '');
            setShowSettings(false);
            const newTab = hash === 'individuals' ? 'individuals' : 'companies';
            setActiveTab(newTab);
            if (hash === 'settings') {
                setShowSettings(true);
            } else {
                setShowSettings(false);
                const newTab = hash === 'individuals' ? 'individuals' : 'companies';
                setActiveTab(newTab);
            }
        };
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    // Update URL hash when activeTab changes (only when not in settings view)
    useEffect(() => {
        if (showSettings) return;
        const newHash = activeTab === 'individuals' ? '#individuals' : '#companies';
        if (window.location.hash !== newHash) {
            window.location.hash = newHash;
        }
    }, [activeTab, showSettings]);

    const handleOpenSettings = () => {
        window.location.hash = '#settings';
    };

    const handleCloseSettings = () => {
        const hash = activeTab === 'individuals' ? '#individuals' : '#companies';
        window.location.hash = hash;
    };
    // function to display the table with customer data, customers is a list of KnowYourCustomer objects
    const displayTable = () => {
        return <DisplayBox
            asideComponent={
                isKycAdmin
                    ? <div
                        className="kyc-settings__gear-btn"
                        onClick={handleOpenSettings}
                        aria-label="Stillingar"
                    >
                        <FontAwesomeIcon icon={faGear} />
                    </div>
                    : undefined
            }
        >
            <Tabs
                tabsSize="lg"
                redirectToTab={activeTab === 'individuals' ? 1 : 0}
                onTabChange={(i) => setActiveTab(i === 0 ? 'companies' : 'individuals')}
                tabs={[{
                    id: 'companies',
                    label: 'Lögaðilar',
                    component: (
                        <CompaniesTable
                            customers={customers}
                            setCustomers={setCustomers}
                            setShowKYCOverview={setShowKYCOverview}
                            companyKYCPrice={companyKYCPrice}
                            employees={employees}
                            showKYCOverviewModal={showKYCOverview && activeTab === 'companies'}
                        />
                    )
                }, {
                    id: 'individuals',
                    label: 'Einstaklingar',
                    component: (
                        <IndividualsTable
                            individuals={individuals}
                            setIndividuals={setIndividuals}
                            setShowKYCOverview={setShowKYCOverview}
                            employees={employees}
                            individualKYCPrice={individualKYCPrice}
                            showKYCOverviewModal={showKYCOverview && activeTab === 'individuals'}
                        />
                    )
                }]}
            />
        </DisplayBox>
    }

    if (showSettings) {
        return (
            <KYCSettings onGoBack={handleCloseSettings} hasCustomWebPage={hasCustomWebPage} />
        );
    }

    return (
        <div>
            <div className="shell">
                <HeadSection
                    title="Áreiðanleikakannanir (KYC)"
                />
                <div className="layout">
                    {/* instructions area */}
                    <Instructions
                        isIndividual={activeTab === 'individuals'}
                    />

                    {hasCustomWebPage && <CustomPageInfoBox slug={customPageSlug} isKycAdmin={isKycAdmin} />}
                </div >
            </div >

            <div className="section--gray">
                <div className="shell">
                    {displayTable()}
                </div>
            </div>
        </div>

    )
}

interface ICustomPageInfoBoxProps {
    slug: string | null;
    isKycAdmin: boolean;
}

const CustomPageInfoBox: React.FC<ICustomPageInfoBoxProps> = ({ slug, isKycAdmin }) => {
    const [copied, setCopied] = useState(false);

    const hasSlug = slug != null && slug.trim().length > 0;
    const fullUrl = `https://kyc.keldan.is/${slug}`;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(fullUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // fallback ignored
        }
    };

    if (!hasSlug) {
        return (
            <div style={{ marginBottom: '16px' }}>
                <Alert
                    type="info"
                    headText={
                        <>
                            Sérsniðinn vefur hefur verið virkjaður en slóðin hefur ekki verið valin.
                        </>
                    }
                    text={
                        isKycAdmin
                            ? <span>
                                Hægt er að velja slóðina hér í{' '}
                                <Link href="#settings">
                                    stillingum
                                </Link>
                                .
                            </span>
                            : <span>
                                Til þess þarf notandi með stjórnenda réttindi að fara í stillingar.
                            </span>
                    }
                />
            </div>
        );
    }

    return (
        <div className="custom-page-info-box">
            <div className="custom-page-info-box__inline">
                <Alert
                    type="info"
                    headText={
                        <span className="custom-page-info-box__head">
                            <Tooltip
                                text={<>Sérsniðin síða:{' '}</>}
                                tooltip={<>
                                    <p>Eftirfarandi hlekk er hægt að deila með viðskiptavinum sem skrá sig inn með rafrænum skilríkjum og svara könnun fyrir sig eða fyrir hönd félags sem þeir eru í forsvari. Ekki er þörf á að stofna viðskiptavini og hefja könnun á þeim fyrirfram. Þegar viðskiptavinur hefur skilað inn svörum er yfirferð og áhættumat (síðasta skrefið) það eina sem eftir er.</p>
                                    <p>Ath. viðskiptavinur getur aðeins svarað einu sinni á 30 daga fresti í gegnum sérsniðna vefinn. Einnig er vakin athygli á að þeir viðskiptavinir sem svara í gegnum sérsniðna vefinn er ekki merktur niður á umsjónarmann og þarf því að gera það sérstaklega þegar svörum hefur verið skilað inn.</p>
                                </>}
                                noUnderline={false}
                            />
                            <Link href={fullUrl} targetBlank >{fullUrl}</Link>
                            <button
                                onClick={handleCopy}
                                className="custom-page-info-box__copy-btn"
                                title={copied ? 'Afritað!' : 'Afrita hlekk'}
                            >
                                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                            </button>
                        </span>
                    }
                />
            </div>
        </div>
    );
}